import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api, formatErr } from "../lib/api";
import { useGameSocket } from "../lib/useGameSocket";
import { serverNow } from "../lib/clock";
import { OptionButton } from "../components/OptionButton";
import { StageTimer } from "../components/StageTimer";
import { EqBars } from "../components/EqBars";
import { StageProgress } from "../components/StageProgress";
import {
  Play, ChevronRight, Users, ArrowLeft, MoreHorizontal, Trophy,
  Maximize2, DoorOpen, X, ArrowRight, Music2, PartyPopper, Tv,
} from "lucide-react";

const STAGE_LABEL = { idiom: "DEYİMİ YAKALA", meaning: "ANLAMINI YAKALA", context: "BAĞLAMI YAKALA" };
const STAGE_ICON  = { idiom: "🎧", meaning: "💭", context: "🧠" };

/* ─────────────────────────────────────────────────────────────────── */

export default function TeacherLive() {
  const { code } = useParams();
  const nav = useNavigate();
  const { state, connected } = useGameSocket(code);
  const [busy, setBusy] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [listenInfo, setListenInfo] = useState(null);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [leaderboardOverlay, setLeaderboardOverlay] = useState(false);
  const [projection, setProjection] = useState(false);
  const [wasDisconnected, setWasDisconnected] = useState(false);
  const [reconnected, setReconnected] = useState(false);

  useEffect(() => {
    if (!connected && state) setWasDisconnected(true);
    if (connected && wasDisconnected) {
      setReconnected(true);
      setWasDisconnected(false);
      const t = setTimeout(() => setReconnected(false), 2500);
      return () => clearTimeout(t);
    }
  }, [connected, state, wasDisconnected]);

  const call = async (path) => {
    setBusy(true);
    try { await api.post(`/games/${code}/control/${path}`); }
    catch (e) { alert(formatErr(e)); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (state?.status === "LISTENING" && state?.current_idiom?.id) {
      api.get(`/games/${code}/listen`).then((r) => setListenInfo(r.data)).catch(() => setListenInfo(null));
    }
  }, [state?.status, state?.current_idiom?.id, code]);

  const openYouTube = () => {
    if (!listenInfo?.youtube_url) return;
    window.open(listenInfo.youtube_url, "_blank", "noopener,noreferrer");
    setAudioPlaying(true);
    setTimeout(() => setAudioPlaying(false), 4000);
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen?.().catch(() => {});
    else document.exitFullscreen?.().catch(() => {});
    setMenuOpen(false);
  };

  const endGame = async () => {
    setConfirmEnd(false);
    setMenuOpen(false);
    await call("end");
  };

  if (!state) {
    return <div className="min-h-screen dark bg-[#070B14] text-white flex items-center justify-center">Bağlanıyor... {String(connected)}</div>;
  }

  const st = state.status;
  const showQ = st.endsWith("_QUESTION") || st.endsWith("_RESULT");
  const isResult = st.endsWith("_RESULT");
  const q = state.current_question;

  /* ── LOBBY ── */
  if (st === "LOBBY") {
    if (projection) {
      return <ProjectionLobby state={state} code={code} onExit={() => setProjection(false)}
                              onStart={() => call("start")} busy={busy} />;
    }
    return (
      <div className="min-h-screen text-white relative overflow-hidden"
           style={{ background: "linear-gradient(180deg, #070B14 0%, #0B1020 100%)" }}>
        <div className="hero-spectrum absolute inset-0" aria-hidden />
        <div className="max-w-6xl mx-auto px-8 py-12 relative">
          <Link to="/teacher" className="text-zinc-400 text-sm">← Panel</Link>
          <div className="mt-10 grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <div className="text-xs font-heading tracking-widest text-cyan-300">CANLI OYUN LOBİSİ</div>
              <h1 className="mt-2 font-heading text-5xl font-bold">Öğrenciler oyuna katılıyor…</h1>
              <p className="mt-3 text-zinc-400 font-literary italic text-xl">Paket: {state.pack_key}</p>
              <div className="mt-10 rounded-3xl border border-cyan-400/20 bg-[#0B1020]/80 p-8 shadow-[0_0_60px_-30px_rgba(6,213,230,0.35)]">
                <div className="text-xs font-heading tracking-widest text-zinc-500">SINIF KODU</div>
                <div className="mt-2 font-heading text-7xl sm:text-8xl font-bold tracking-widest tabular-nums text-cyan-200" data-testid="game-code">
                  {code.slice(0,3)} {code.slice(3)}
                </div>
                <p className="mt-4 text-zinc-400 text-sm">Öğrencileriniz bu kodla oyuna katılabilir.</p>
              </div>
              <button onClick={() => call("start")} disabled={busy || state.player_count === 0} data-testid="start-game-btn"
                      className="mt-8 w-full rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-black font-heading font-bold py-4 text-xl hover:opacity-90 disabled:opacity-40">
                OYUNU BAŞLAT
              </button>
              <button onClick={() => setProjection(true)} data-testid="lobby-projection-btn"
                      className="mt-3 w-full rounded-full border border-cyan-400/40 text-cyan-200 hover:bg-cyan-500/10 font-heading font-bold py-3 flex items-center justify-center gap-2">
                <Tv className="h-5 w-5"/> 📺 SINIFA YANSIT
              </button>
            </div>
            <div>
              <div className="flex items-center gap-2 text-zinc-400 text-sm"><Users className="h-4 w-4"/> Katılan Öğrenci · <span className="text-white font-heading">{state.player_count}</span></div>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2 min-h-[200px]">
                {state.players.map((p) => (
                  <div key={p.id} className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm truncate" data-testid={`player-${p.name}`}>{p.name}</div>
                ))}
                {state.players.length === 0 && <div className="col-span-full text-zinc-600 text-sm">Henüz kimse yok.</div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── FINISHED — final podium ── */
  if (st === "FINISHED") {
    return <FinalPodium state={state} code={code} nav={nav} projection={projection}
                        onToggleProjection={() => setProjection((v) => !v)} />;
  }

  /* ── Projection Mode (playing states) ── */
  if (projection) {
    return <ProjectionPlay
      state={state} listenInfo={listenInfo} audioPlaying={audioPlaying}
      openYouTube={openYouTube}
      onContinue={() => call("next_stage")}
      onExit={() => setProjection(false)}
      busy={busy}
      connected={connected} reconnected={reconnected}
    />;
  }

  /* ── Playing states ── */
  return (
    <div className="min-h-screen text-white relative overflow-hidden"
         style={{ background: "linear-gradient(180deg, #070B14 0%, #0B1020 100%)" }}>

      {/* Reconnect banners */}
      {!connected && wasDisconnected && (
        <div className="fixed top-0 inset-x-0 z-50 bg-amber-500/95 text-black text-center text-xs py-1.5 font-heading" data-testid="teacher-reconnect-banner">
          Bağlantı kesildi… Yeniden bağlanılıyor.
        </div>
      )}
      {reconnected && (
        <div className="fixed top-0 inset-x-0 z-50 bg-emerald-500/95 text-black text-center text-xs py-1.5 font-heading" data-testid="teacher-reconnected-banner">
          ✓ Oyuna yeniden bağlandınız.
        </div>
      )}

      {/* Sticky teacher control bar */}
      <TeacherBar
        state={state} code={code}
        onExit={() => setConfirmEnd(true)}
        onLeaderboard={() => setLeaderboardOverlay(true)}
        onMenu={() => setMenuOpen((v) => !v)}
        menuOpen={menuOpen}
        onFullscreen={toggleFullscreen}
        onEnd={() => { setMenuOpen(false); setConfirmEnd(true); }}
        onProjection={() => { setMenuOpen(false); setProjection(true); }}
      />

      <div className="max-w-6xl mx-auto px-6 sm:px-10 pt-24 pb-16 relative">

        {/* LISTENING */}
        {st === "LISTENING" && (
          <ListeningView
            state={state} listenInfo={listenInfo}
            openYouTube={openYouTube} audioPlaying={audioPlaying}
            onContinue={() => call("next_stage")} busy={busy}
          />
        )}

        {st === "COUNTDOWN" && <Countdown deadline={state.stage_deadline} onDone={() => call("next_stage")} />}

        {showQ && q && (
          <QuestionView
            state={state} q={q} isResult={isResult} isQuestion={st.endsWith("_QUESTION")}
            onSkip={() => call("skip")} onAdvance={() => call("next_stage")} busy={busy}
          />
        )}

        {st === "IDIOM_LEADERBOARD" && (
          <MiniLeaderboard state={state} onContinue={() => call("next_stage")} busy={busy} />
        )}

        {st === "LEADERBOARD" && (
          <SongEndLeaderboard state={state} onContinue={() => call("next_stage")} busy={busy} />
        )}
      </div>

      {/* Overlay: live leaderboard (does not advance state) */}
      {leaderboardOverlay && (
        <LeaderboardOverlay state={state} onClose={() => setLeaderboardOverlay(false)} />
      )}

      {/* Confirm end modal */}
      {confirmEnd && (
        <ConfirmModal
          title="Oyunu sonlandırmak istediğinize emin misiniz?"
          desc="Öğrencilerin oyunu sona erecek ve final tabloları gösterilecek."
          confirmLabel="OYUNU SONLANDIR"
          onConfirm={endGame}
          onCancel={() => setConfirmEnd(false)}
        />
      )}
    </div>
  );
}

/* ─────────────────────────── COMPONENTS ─────────────────────────── */

function TeacherBar({ state, code, onExit, onLeaderboard, onMenu, menuOpen, onFullscreen, onEnd, onProjection }) {
  const song = state.current_song;
  return (
    <div className="fixed top-0 inset-x-0 z-40 backdrop-blur-lg bg-[#070B14]/85 border-b border-white/10">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between gap-3">
        <button onClick={onExit} data-testid="exit-game-btn"
                className="flex items-center gap-2 text-zinc-400 hover:text-rose-300 text-sm">
          <ArrowLeft className="h-4 w-4"/> Oyundan Çık
        </button>

        <div className="hidden sm:flex items-center gap-3 text-xs text-zinc-400 flex-1 justify-center">
          {song && (
            <>
              <Music2 className="h-4 w-4 text-cyan-300"/>
              <span className="text-white font-heading">{song.artist}</span>
              <span className="text-zinc-600">·</span>
              <span className="italic font-literary text-cyan-300">{song.song}</span>
              <span className="text-zinc-600">·</span>
              <span>Deyim <span className="text-white font-heading">{song.position}/{song.total_idioms}</span></span>
              <span className="text-zinc-600">·</span>
            </>
          )}
          <span className="flex items-center gap-1"><Users className="h-4 w-4"/> <span className="text-white font-heading">{state.player_count}</span></span>
        </div>

        <div className="flex items-center gap-1">
          <button onClick={onLeaderboard} data-testid="show-leaderboard-btn"
                  className="rounded-full border border-white/15 px-3 py-1.5 text-sm hover:border-cyan-400 hover:text-cyan-300 flex items-center gap-1.5">
            <Trophy className="h-4 w-4"/> <span className="hidden sm:inline">Liderlik</span>
          </button>
          <div className="relative">
            <button onClick={onMenu} data-testid="game-menu-btn"
                    className="rounded-full border border-white/15 p-1.5 hover:border-white/40">
              <MoreHorizontal className="h-4 w-4"/>
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-2 w-56 rounded-xl border border-white/10 bg-[#0B1020] shadow-2xl overflow-hidden">
                <button onClick={onLeaderboard} className="w-full px-4 py-2.5 text-sm text-left hover:bg-white/5 flex items-center gap-2"><Trophy className="h-4 w-4 text-cyan-300"/> Liderliği Gör</button>
                <button onClick={onProjection} data-testid="menu-projection-btn"
                        className="w-full px-4 py-2.5 text-sm text-left hover:bg-white/5 flex items-center gap-2"><Tv className="h-4 w-4 text-cyan-300"/> 📺 Sınıfa Yansıt</button>
                <button onClick={onFullscreen} className="w-full px-4 py-2.5 text-sm text-left hover:bg-white/5 flex items-center gap-2"><Maximize2 className="h-4 w-4"/> Tam Ekran</button>
                <button onClick={onEnd} data-testid="menu-end-game-btn"
                        className="w-full px-4 py-2.5 text-sm text-left text-rose-300 hover:bg-rose-500/10 flex items-center gap-2 border-t border-white/5"><DoorOpen className="h-4 w-4"/> Oyunu Sonlandır</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ListeningView({ state, listenInfo, openYouTube, audioPlaying, onContinue, busy }) {
  const song = state.current_song;
  const idx = state.song_index ?? 0;
  const total = state.songs_total ?? 1;
  const isFirst = idx === 0;
  return (
    <div className="mt-4 rounded-3xl border border-white/10 bg-zinc-950/70 p-10">
      <div className="text-center">
        {!isFirst && (
          <div className="text-xs font-heading tracking-widest text-violet-300 mb-2">SIRADAKİ ŞARKI · {idx + 1}/{total}</div>
        )}
        <div className="text-xs font-heading tracking-widest text-cyan-300">🎵 ŞARKIYI DİNLET</div>
        <div className="mt-3 text-zinc-400 font-heading tracking-widest text-xs">{song?.artist?.toUpperCase()}</div>
        <h2 className="mt-1 font-literary italic text-4xl sm:text-5xl text-cyan-200">{song?.song}</h2>
        {song && (
          <p className="mt-3 text-zinc-300 font-heading text-sm tracking-widest">
            {song.total_idioms} DEYİM · {song.questions_total} SORU
          </p>
        )}
        <p className="mt-2 text-zinc-500 text-sm">Şarkıyı bir kez baştan sona dinlet. Bu şarkıdaki tüm deyimleri arka arkaya soracağız.</p>
        <div className="mt-6 flex justify-center"><EqBars playing={audioPlaying} /></div>

        <div className="mt-8 flex flex-wrap justify-center gap-3">
          {listenInfo?.youtube_url ? (
            <button onClick={openYouTube} data-testid="youtube-play-btn"
                    className="rounded-full bg-gradient-to-r from-red-500 to-red-600 text-white font-heading font-bold px-6 py-3 flex items-center gap-2 hover:opacity-90">
              <Play className="h-4 w-4"/> 🎧 DİNLEMEYİ BAŞLAT
            </button>
          ) : (
            <div className="text-zinc-500 text-sm">Şarkı bilgisi yükleniyor…</div>
          )}
        </div>

        <div className="mt-10 border-t border-white/5 pt-8">
          <button onClick={onContinue} disabled={busy} data-testid="next-stage-btn"
                  className="rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-black font-heading font-bold px-8 py-4 text-lg hover:opacity-90 disabled:opacity-40">
            ✓ DİNLEME TAMAMLANDI — SORULARI BAŞLAT →
          </button>
          <p className="mt-3 text-xs text-zinc-600">Öğretmen kontrolü. YouTube ayrı sekmede açılır, oyun durumu korunur.</p>
        </div>
      </div>
    </div>
  );
}

function QuestionView({ state, q, isResult, isQuestion, onSkip, onAdvance, busy }) {
  return (
    <div className="mt-2">
      {state.current_song && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs font-heading tracking-widest text-zinc-500">
            <span className="text-white">{state.current_song.artist}</span>
            <span className="mx-2 text-cyan-400/60">•</span>
            <span className="italic font-literary text-cyan-300 not-italic">{state.current_song.song}</span>
            <span className="mx-2 text-cyan-400/60">•</span>
            DEYİM <span className="text-white">{state.current_song.position}</span>/<span className="text-white">{state.current_song.total_idioms}</span>
          </div>
          <StageProgress current={q.stage} isResult={isResult} />
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="text-xs font-heading tracking-widest text-cyan-300">{STAGE_ICON[q.stage]} {STAGE_LABEL[q.stage]}</div>
        {isQuestion && <StageTimer deadline={state.stage_deadline}/>}
      </div>
      <div className="mt-4 rounded-3xl border border-white/10 bg-zinc-950/70 p-8 tracing-border">
        <p className="font-literary text-2xl sm:text-3xl leading-snug">{q.text}</p>
      </div>
      <div className="mt-6 grid md:grid-cols-2 gap-3">
        {q.options.map((opt, i) => {
          const L = "ABCDE"[i];
          return (
            <OptionButton key={L} letter={L} showResult={isResult}
                          correct={isResult && q.correct === L}
                          testId={`teacher-opt-${L}`}>{opt}</OptionButton>
          );
        })}
      </div>
      <div className="mt-6 flex justify-end gap-3">
        {isQuestion && (
          <button onClick={onSkip} className="text-zinc-400 hover:text-white text-sm" data-testid="skip-btn">Soruyu Atla →</button>
        )}
        <button onClick={onAdvance} disabled={busy} data-testid="advance-btn"
                className="rounded-full bg-white text-black font-heading font-bold px-5 py-2 flex items-center gap-2 hover:opacity-90">
          {isResult ? "İleri" : "Sonucu Göster"} <ArrowRight className="h-4 w-4"/>
        </button>
      </div>
      {isResult && state.current_idiom && (
        <div className="mt-6 rounded-2xl border border-cyan-400/30 bg-cyan-500/5 p-5">
          <div className="text-xs text-cyan-300 font-heading tracking-widest">DOĞRU CEVAP • {q.correct}</div>
          <div className="mt-1 font-literary italic text-xl">"{state.current_idiom.idiom}"</div>
          <div className="text-zinc-300 mt-1 text-sm">{state.current_idiom.meaning}</div>
        </div>
      )}
    </div>
  );
}

function PodiumRow({ p, rank, big }) {
  const medal = rank === 1 ? "🥇" : rank === 2 ? "🥈" : "🥉";
  const styles = {
    1: "border-yellow-300/50 bg-gradient-to-r from-yellow-500/15 to-amber-500/5 shadow-[0_0_60px_-20px_rgba(250,204,21,0.55)]",
    2: "border-zinc-300/40 bg-gradient-to-r from-zinc-300/10 to-zinc-500/5",
    3: "border-orange-400/40 bg-gradient-to-r from-orange-500/10 to-orange-500/5",
  };
  return (
    <div data-testid={`podium-${rank}`}
         className={`flex items-center gap-4 rounded-2xl border px-5 ${big ? "py-5" : "py-4"} ${styles[rank] || "border-white/10 bg-white/[0.03]"}`}>
      <div className="font-heading text-3xl w-12">{medal}</div>
      <div className="flex-1 font-heading text-xl sm:text-2xl truncate">{p.name}</div>
      {rank === 1 && <Trophy className="h-6 w-6 text-yellow-300"/>}
      <div className="font-heading text-2xl sm:text-3xl tabular-nums">{(p.score || 0).toLocaleString("tr-TR")}</div>
    </div>
  );
}

function RegularRow({ p, rank }) {
  return (
    <div data-testid={`rank-${rank}`} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-zinc-950/70 px-5 py-3">
      <div className="font-heading text-xl w-12 tabular-nums text-cyan-300">{rank}</div>
      <div className="flex-1 font-heading text-lg truncate">{p.name}</div>
      <div className="font-heading text-xl tabular-nums">{(p.score || 0).toLocaleString("tr-TR")} <span className="text-xs text-zinc-500">puan</span></div>
    </div>
  );
}

function MiniLeaderboard({ state, onContinue, busy }) {
  const top5 = state.top5 || [];
  return (
    <div className="mt-6" data-testid="mini-leaderboard">
      <div className="text-center">
        <div className="text-xs font-heading tracking-widest text-cyan-300">DEYİM TAMAMLANDI</div>
        <h1 className="mt-2 font-heading text-4xl font-bold">🏆 ANLIK LİDERLİK</h1>
        {state.current_idiom && (
          <p className="mt-2 text-zinc-400 font-literary italic">"{state.current_idiom.idiom}"</p>
        )}
      </div>
      <div className="mt-8 space-y-3">
        {top5.slice(0,3).map((p, i) => <PodiumRow key={i} p={p} rank={i+1} />)}
        {top5.slice(3,5).map((p, i) => <RegularRow key={i+3} p={p} rank={i+4} />)}
        {top5.length === 0 && <div className="text-zinc-500 text-center py-8">Henüz puan yok.</div>}
      </div>
      <button onClick={onContinue} disabled={busy} data-testid="mini-continue-btn"
              className="mt-8 w-full rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-black font-heading font-bold py-4 hover:opacity-90 disabled:opacity-40">
        {state.current_song?.is_last_idiom_of_song ? "🎵 ŞARKIYI TAMAMLA →" : "SONRAKİ DEYİM →"}
      </button>
    </div>
  );
}

function SongEndLeaderboard({ state, onContinue, busy }) {
  const top5 = state.top5 || [];
  const song = state.current_song;
  const isLastSong = state.is_last_song;
  const next = state.next_song;
  return (
    <div className="mt-4" data-testid="song-end-leaderboard">
      <div className="text-center">
        <div className="text-xs font-heading tracking-widest text-violet-300">ŞARKI TAMAMLANDI</div>
        <h1 className="mt-2 font-heading text-5xl font-bold">🎵 {song?.song?.toUpperCase()}</h1>
        <p className="mt-2 text-zinc-400 font-heading text-sm tracking-widest">
          {song?.total_idioms} DEYİM · {song?.questions_total} SORU
        </p>
      </div>

      <div className="mt-8">
        <div className="text-xs font-heading tracking-widest text-cyan-300 mb-3 text-center">🏆 ŞARKI SONU LİDERLİK</div>
        <div className="space-y-3">
          {top5.slice(0,3).map((p, i) => <PodiumRow key={i} p={p} rank={i+1} big />)}
          {top5.slice(3,5).map((p, i) => <RegularRow key={i+3} p={p} rank={i+4} />)}
          {top5.length === 0 && <div className="text-zinc-500 text-center py-8">Henüz puan yok.</div>}
        </div>
      </div>

      {next && !isLastSong && (
        <div className="mt-10 rounded-2xl border border-violet-400/25 bg-violet-500/5 p-6 text-center">
          <div className="text-xs font-heading tracking-widest text-violet-300">SIRADAKİ ŞARKI</div>
          <div className="mt-2 text-zinc-400 font-heading text-xs tracking-widest">{next.artist?.toUpperCase()}</div>
          <div className="mt-1 font-literary italic text-3xl text-violet-200">{next.song}</div>
          <div className="mt-2 text-zinc-500 text-xs">{next.total_idioms} deyim · {next.questions_total} soru</div>
        </div>
      )}

      <button onClick={onContinue} disabled={busy} data-testid="song-end-continue-btn"
              className="mt-8 w-full rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-black font-heading font-bold py-4 text-lg hover:opacity-90 disabled:opacity-40">
        {isLastSong ? "🏁 FİNAL EKRANINA GEÇ →" : "🎵 SONRAKİ ŞARKIYA GEÇ →"}
      </button>
    </div>
  );
}

function LeaderboardOverlay({ state, onClose }) {
  const players = state.players || [];
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-start sm:items-center justify-center p-4 overflow-auto" data-testid="leaderboard-overlay">
      <div className="w-full max-w-3xl rounded-3xl border border-white/10 bg-[#0B1020] p-8 my-8">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-heading tracking-widest text-cyan-300">CANLI LİDERLİK</div>
            <h2 className="mt-1 font-heading text-3xl font-bold">🏆 Anlık Skorlar</h2>
          </div>
          <button onClick={onClose} data-testid="close-overlay-btn"
                  className="rounded-full border border-white/15 p-2 hover:border-white/40">
            <X className="h-5 w-5"/>
          </button>
        </div>
        <div className="mt-6 space-y-2 max-h-[65vh] overflow-y-auto pr-2">
          {players.slice(0,3).map((p, i) => <PodiumRow key={p.id} p={{ name: p.name, score: p.score }} rank={i+1} />)}
          {players.slice(3).map((p, i) => <RegularRow key={p.id} p={{ name: p.name, score: p.score }} rank={i+4} />)}
          {players.length === 0 && <div className="text-zinc-500 text-center py-8">Henüz oyuncu yok.</div>}
        </div>
        <button onClick={onClose}
                className="mt-6 w-full rounded-full border border-cyan-400/40 text-cyan-200 py-3 hover:bg-cyan-500/10 font-heading">
          ← OYUNA DÖN
        </button>
      </div>
    </div>
  );
}

function ConfirmModal({ title, desc, confirmLabel, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4" data-testid="confirm-modal">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0B1020] p-8">
        <h3 className="font-heading text-2xl font-bold">{title}</h3>
        {desc && <p className="mt-2 text-zinc-400">{desc}</p>}
        <div className="mt-8 flex gap-3 justify-end">
          <button onClick={onCancel} data-testid="modal-cancel-btn"
                  className="rounded-full border border-white/15 px-5 py-2.5 hover:border-white/40">
            VAZGEÇ
          </button>
          <button onClick={onConfirm} data-testid="modal-confirm-btn"
                  className="rounded-full bg-rose-500 hover:bg-rose-400 text-white font-heading font-bold px-5 py-2.5">
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function Countdown({ deadline, onDone }) {
  const [n, setN] = useState(3);
  useEffect(() => {
    const iv = setInterval(() => {
      const s = Math.max(0, Math.ceil((new Date(deadline).getTime() - serverNow()) / 1000));
      setN(s);
      if (s <= 0) { clearInterval(iv); onDone?.(); }
    }, 200);
    return () => clearInterval(iv);
  }, [deadline]);
  return (
    <div className="mt-24 flex flex-col items-center">
      <div className="font-heading font-bold tabular-nums text-[10rem] leading-none bg-gradient-to-br from-cyan-300 to-violet-400 bg-clip-text text-transparent" data-testid="countdown">
        {n || "!"}
      </div>
      <div className="mt-4 text-zinc-400 font-literary italic">Hazır ol…</div>
    </div>
  );
}

/* ─────────────────────── FINAL PODIUM ─────────────────────── */

function FinalPodium({ state, code, nav, projection, onToggleProjection }) {
  const top = (state.top5 || []).slice(0, 3);
  const rest = (state.top5 || []).slice(3, 5);
  const [gid, setGid] = useState(state.game_id || null);
  useEffect(() => {
    if (!gid) api.get(`/games/${code}`).then((r) => setGid(r.data.id)).catch(() => {});
  }, [code, gid]);
  const scale = projection ? "scale-110" : "";
  return (
    <div className="min-h-screen dark bg-[#070B14] text-white relative overflow-hidden">
      <div className="grain absolute inset-0"/>
      <Confetti />
      <div className={`max-w-4xl mx-auto px-6 py-16 text-center relative z-10 ${scale}`}>
        {onToggleProjection && (
          <div className="absolute top-4 right-4">
            <button onClick={onToggleProjection} data-testid="toggle-projection-btn"
                    className="rounded-full border border-white/15 px-3 py-1.5 text-sm hover:border-cyan-400 hover:text-cyan-300 flex items-center gap-1.5">
              {projection ? "← Öğretmen Görünümü" : <><Tv className="h-4 w-4"/> Sınıfa Yansıt</>}
            </button>
          </div>
        )}
        <div className="inline-flex items-center gap-2 text-cyan-300 font-heading tracking-widest text-sm">
          <PartyPopper className="h-4 w-4"/> OYUN TAMAMLANDI
        </div>
        <h1 className={`mt-4 font-heading font-bold ${projection ? "text-7xl sm:text-8xl" : "text-6xl"}`}>🏆 FİNAL PODYUM</h1>

        {/* Podium visual: 2 - 1 - 3 */}
        <div className="mt-14 grid grid-cols-3 gap-4 items-end">
          {[1,0,2].map((idx) => {
            const p = top[idx]; if (!p) return <div key={idx}/>;
            const heights = { 0: "h-64", 1: "h-44", 2: "h-36" };
            const colors = {
              0: "from-yellow-300 to-yellow-500 shadow-[0_0_80px_-20px_rgba(250,204,21,0.6)]",
              1: "from-zinc-300 to-zinc-500",
              2: "from-orange-400 to-orange-600",
            };
            const medals = { 0: "🥇", 1: "🥈", 2: "🥉" };
            return (
              <div key={idx} className="flex flex-col items-center" data-testid={`final-rank-${idx+1}`}>
                {idx === 0 && <Trophy className="h-10 w-10 text-yellow-300 mb-2"/>}
                <div className="font-heading text-2xl mb-2">{medals[idx]}</div>
                <div className="font-literary italic text-lg mb-2 truncate max-w-full">{p.name}</div>
                <div className={`w-full rounded-t-2xl bg-gradient-to-b ${colors[idx]} ${heights[idx]} flex items-center justify-center text-black font-heading font-black text-5xl`}>
                  {idx+1}
                </div>
                <div className="mt-3 font-heading text-2xl tabular-nums">{(p.score || 0).toLocaleString("tr-TR")}</div>
              </div>
            );
          })}
        </div>

        {rest.length > 0 && (
          <div className="mt-8 space-y-2 max-w-lg mx-auto text-left">
            {rest.map((p, i) => <RegularRow key={i} p={p} rank={i+4} />)}
          </div>
        )}

        <div className="mt-12 flex flex-wrap justify-center gap-3">
          <button onClick={() => nav(`/teacher/report/${gid || ""}`)} disabled={!gid}
                  data-testid="view-report-btn"
                  className="rounded-full bg-white text-black font-heading font-bold px-6 py-3 disabled:opacity-40 flex items-center gap-2">
            📊 OYUN RAPORUNU GÖR
          </button>
          <button onClick={() => nav(`/teacher`)}
                  data-testid="new-game-btn"
                  className="rounded-full border border-cyan-400/40 text-cyan-200 hover:bg-cyan-500/10 font-heading font-bold px-6 py-3">
            🔄 YENİ OYUN BAŞLAT
          </button>
          <a href="/teacher" data-testid="home-btn"
             className="rounded-full border border-white/20 px-6 py-3 font-heading">⌂ ANA SAYFA</a>
        </div>
      </div>
    </div>
  );
}

function Confetti() {
  // 30 subtle falling dots, dark-mode friendly
  const items = useMemo(() => Array.from({ length: 30 }).map((_, i) => ({
    left: Math.random() * 100,
    delay: Math.random() * 3,
    dur: 4 + Math.random() * 3,
    color: ["#22d3ee", "#a78bfa", "#facc15", "#f472b6"][i % 4],
    size: 5 + Math.random() * 6,
  })), []);
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {items.map((it, i) => (
        <span key={i}
          style={{
            position: "absolute", top: "-10px", left: `${it.left}%`,
            width: it.size, height: it.size, background: it.color, borderRadius: 2,
            animation: `ssd-fall ${it.dur}s linear ${it.delay}s infinite`, opacity: 0.7,
          }}/>
      ))}
      <style>{`@keyframes ssd-fall { 0% { transform: translateY(-10vh) rotate(0deg); opacity: 0; }
        10% { opacity: 0.7; }
        100% { transform: translateY(110vh) rotate(720deg); opacity: 0; } }`}</style>
    </div>
  );
}

/* ─────────────────────── PROJECTION MODE ─────────────────────── */

function ProjectionExit({ onExit }) {
  return (
    <button onClick={onExit} data-testid="exit-projection-btn"
            className="absolute top-6 right-6 z-20 rounded-full border border-white/20 px-4 py-2 text-sm text-zinc-300 hover:text-white hover:border-cyan-400 flex items-center gap-2 backdrop-blur-sm bg-black/30">
      <ArrowLeft className="h-4 w-4"/> Öğretmen Görünümüne Dön
    </button>
  );
}

function ProjectionFullscreenBtn() {
  const toggle = () => {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen?.().catch(() => {});
    else document.exitFullscreen?.().catch(() => {});
  };
  return (
    <button onClick={toggle} data-testid="projection-fullscreen-btn"
            className="absolute top-6 left-6 z-20 rounded-full border border-white/20 px-4 py-2 text-sm text-zinc-300 hover:text-white hover:border-cyan-400 flex items-center gap-2 backdrop-blur-sm bg-black/30">
      <Maximize2 className="h-4 w-4"/> Tam Ekran
    </button>
  );
}

function ProjectionLobby({ state, code, onExit, onStart, busy }) {
  const joinUrl = `${window.location.origin}/join?code=${code}`;
  return (
    <div className="min-h-screen text-white relative overflow-hidden"
         style={{ background: "linear-gradient(180deg, #070B14 0%, #0B1020 100%)" }}
         data-testid="projection-lobby">
      <div className="hero-spectrum absolute inset-0" aria-hidden />
      <ProjectionFullscreenBtn />
      <ProjectionExit onExit={onExit} />
      <div className="max-w-[1600px] mx-auto px-12 py-14 relative">
        <div className="text-center">
          <div className="text-cyan-300 font-heading tracking-[0.4em] text-lg">ŞARKI SÖYLEYEN DEYİMLER</div>
          <h1 className="mt-3 font-heading text-7xl sm:text-8xl font-bold">OYUNA KATIL</h1>
        </div>
        <div className="mt-14 grid lg:grid-cols-2 gap-16 items-center">
          <div className="text-center">
            <div className="text-xs font-heading tracking-widest text-zinc-500">SINIF KODU</div>
            <div data-testid="projection-game-code"
                 className="mt-3 font-heading font-bold tracking-widest tabular-nums text-cyan-200 whitespace-nowrap"
                 style={{ fontSize: "clamp(4rem, 10vw, 11rem)", lineHeight: 1 }}>
              {code.slice(0,3)} {code.slice(3)}
            </div>
            <div className="mt-8 inline-flex items-center gap-3 text-2xl text-zinc-300">
              <Users className="h-7 w-7 text-cyan-300"/>
              <span>Katılan Öğrenci: <span className="text-white font-heading" data-testid="projection-player-count">{state.player_count}</span></span>
            </div>
          </div>
          <div className="flex flex-col items-center">
            <div className="rounded-3xl bg-white p-6 shadow-[0_0_120px_-30px_rgba(6,213,230,0.55)]" data-testid="projection-qr">
              <QRCodeSVG value={joinUrl} size={340} level="M" includeMargin={false} />
            </div>
            <div className="mt-4 text-zinc-400 text-sm">Kamerayla oku · {joinUrl.replace(/^https?:\/\//, "")}</div>
          </div>
        </div>

        {/* Participant list — subtle */}
        <div className="mt-12 grid grid-cols-3 sm:grid-cols-5 gap-2 max-w-5xl mx-auto min-h-[80px]">
          {state.players.slice(0, 20).map((p) => (
            <div key={p.id} className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-center text-sm truncate">{p.name}</div>
          ))}
          {state.players.length > 20 && (
            <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-center text-sm text-zinc-500">
              +{state.players.length - 20} kişi
            </div>
          )}
        </div>

        <div className="mt-12 flex justify-center">
          <button onClick={onStart} disabled={busy || state.player_count === 0}
                  data-testid="projection-start-btn"
                  className="rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-black font-heading font-bold px-14 py-5 text-2xl hover:opacity-90 disabled:opacity-40">
            OYUNU BAŞLAT
          </button>
        </div>
      </div>
    </div>
  );
}

function ProjectionPlay({ state, listenInfo, audioPlaying, openYouTube, onContinue, onExit, busy, connected, reconnected }) {
  const st = state.status;
  const showQ = st.endsWith("_QUESTION") || st.endsWith("_RESULT");
  const isResult = st.endsWith("_RESULT");
  const isQuestion = st.endsWith("_QUESTION");
  const q = state.current_question;
  const song = state.current_song;
  const stageLabels = {
    idiom: "🎧 DEYİMİ YAKALA",
    meaning: "💭 ANLAMINI YAKALA",
    context: "🧠 BAĞLAMI YAKALA",
  };

  return (
    <div className="min-h-screen text-white relative overflow-hidden"
         style={{ background: "linear-gradient(180deg, #070B14 0%, #0B1020 100%)" }}
         data-testid="projection-play">
      {!connected && (
        <div className="fixed top-0 inset-x-0 z-50 bg-amber-500/95 text-black text-center text-sm py-2 font-heading">
          Bağlantı kesildi… Yeniden bağlanılıyor.
        </div>
      )}
      {reconnected && (
        <div className="fixed top-0 inset-x-0 z-50 bg-emerald-500/95 text-black text-center text-sm py-2 font-heading">
          ✓ Oyuna yeniden bağlandınız.
        </div>
      )}
      <ProjectionFullscreenBtn />
      <ProjectionExit onExit={onExit} />

      <div className="max-w-[1600px] mx-auto px-12 pt-14 pb-12">
        {/* Song header — always visible */}
        {song && (
          <div className="text-center">
            <div className="font-heading tracking-[0.35em] text-cyan-300 text-lg">
              {song.artist?.toUpperCase()} <span className="text-cyan-500/50 mx-3">•</span> <span className="text-cyan-200">{song.song?.toUpperCase()}</span>
            </div>
            {(showQ || st === "IDIOM_LEADERBOARD") && (
              <div className="mt-2 text-zinc-400 font-heading text-base tracking-widest">
                DEYİM <span className="text-white">{song.position}</span> / <span className="text-white">{song.total_idioms}</span>
              </div>
            )}
          </div>
        )}

        {/* LISTENING */}
        {st === "LISTENING" && (
          <div className="mt-10 text-center">
            <div className="text-cyan-300 font-heading tracking-[0.35em] text-2xl">🎧 ŞİMDİ DİNLİYORUZ</div>
            <div className="mt-6 text-zinc-400 font-heading tracking-widest">{song?.artist?.toUpperCase()}</div>
            <div className="mt-2 font-literary italic text-cyan-200" style={{ fontSize: "clamp(3.5rem, 8vw, 8rem)", lineHeight: 1 }}>
              {song?.song}
            </div>
            <div className="mt-4 text-zinc-300 font-heading tracking-widest text-xl">
              {song?.total_idioms} DEYİM · {song?.questions_total} SORU
            </div>
            <div className="mt-8 flex justify-center"><EqBars playing={audioPlaying} /></div>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              {listenInfo?.youtube_url ? (
                <button onClick={openYouTube} data-testid="projection-youtube-btn"
                        className="rounded-full bg-gradient-to-r from-red-500 to-red-600 text-white font-heading font-bold px-8 py-4 text-xl flex items-center gap-3 hover:opacity-90">
                  <Play className="h-5 w-5"/> ▶ YouTube'da Dinlet
                </button>
              ) : (
                <div className="text-zinc-500">Şarkı bilgisi yükleniyor…</div>
              )}
              <button onClick={onContinue} disabled={busy}
                      data-testid="projection-continue-btn"
                      className="rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-black font-heading font-bold px-8 py-4 text-xl hover:opacity-90 disabled:opacity-40">
                ✓ SORULARI BAŞLAT →
              </button>
            </div>
            <div className="mt-4 text-zinc-600 text-sm">Dinleme aşamasına yeniden bağlandığınızda YouTube otomatik açılmaz.</div>
          </div>
        )}

        {/* COUNTDOWN */}
        {st === "COUNTDOWN" && <ProjectionCountdown deadline={state.stage_deadline} onDone={onContinue} />}

        {/* QUESTION / RESULT */}
        {showQ && q && (
          <div className="mt-6">
            <div className="text-center text-cyan-300 font-heading tracking-[0.35em]" style={{ fontSize: "clamp(1.25rem, 2vw, 2rem)" }}>
              {stageLabels[q.stage]}
            </div>
            {isQuestion && (
              <div className="mt-4 flex justify-center">
                <div className="text-6xl sm:text-7xl font-heading font-bold tabular-nums text-cyan-200">
                  <StageTimer deadline={state.stage_deadline}/>
                </div>
              </div>
            )}
            <div className="mt-6 rounded-3xl border border-white/10 bg-zinc-950/70 p-10 text-center">
              <p className="font-literary" style={{ fontSize: "clamp(2rem, 4vw, 4rem)", lineHeight: 1.15 }}>
                {q.text}
              </p>
            </div>
            <div className="mt-8 grid md:grid-cols-2 gap-4">
              {q.options.map((opt, i) => {
                const L = "ABCDE"[i];
                const isCorrect = isResult && q.correct === L;
                return (
                  <div key={L}
                    className={`rounded-2xl border p-6 flex items-start gap-5 transition
                      ${isCorrect ? "border-emerald-400 bg-emerald-500/10 shadow-[0_0_50px_-15px_rgba(16,185,129,0.55)]"
                                  : "border-white/10 bg-zinc-950/60"}`}
                    data-testid={`projection-opt-${L}`}>
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center font-heading font-bold text-2xl
                      ${isCorrect ? "bg-emerald-400 text-black" : "bg-white/5 text-cyan-300 border border-white/10"}`}>
                      {L}
                    </div>
                    <div className="font-literary text-3xl sm:text-4xl leading-tight">{opt}</div>
                  </div>
                );
              })}
            </div>
            {isResult && state.current_idiom && (
              <div className="mt-8 rounded-2xl border border-cyan-400/30 bg-cyan-500/5 p-6 text-center">
                <div className="text-cyan-300 font-heading tracking-widest">DOĞRU CEVAP • {q.correct}</div>
                <div className="mt-2 font-literary italic text-3xl">"{state.current_idiom.idiom}"</div>
                <div className="mt-1 text-zinc-300 text-xl">{state.current_idiom.meaning}</div>
              </div>
            )}
            <div className="mt-6 flex justify-center">
              <button onClick={onContinue} disabled={busy} data-testid="projection-advance-btn"
                      className="rounded-full bg-white text-black font-heading font-bold px-8 py-3 flex items-center gap-2 hover:opacity-90">
                {isResult ? "İleri" : "Sonucu Göster"} <ArrowRight className="h-5 w-5"/>
              </button>
            </div>
          </div>
        )}

        {/* LEADERBOARDS */}
        {(st === "IDIOM_LEADERBOARD" || st === "LEADERBOARD") && (
          <ProjectionLeaderboard state={state} onContinue={onContinue} busy={busy} />
        )}
      </div>
    </div>
  );
}

function ProjectionLeaderboard({ state, onContinue, busy }) {
  const st = state.status;
  const top5 = state.top5 || [];
  const isMini = st === "IDIOM_LEADERBOARD";
  return (
    <div className="mt-6" data-testid="projection-leaderboard">
      <div className="text-center">
        <div className={`font-heading tracking-[0.35em] ${isMini ? "text-cyan-300" : "text-violet-300"}`} style={{ fontSize: "clamp(1.25rem, 2vw, 1.75rem)" }}>
          {isMini ? "🏆 ANLIK LİDERLİK" : "🏆 ŞARKI SONU LİDERLİK"}
        </div>
        {!isMini && state.current_song && (
          <div className="mt-3 font-literary italic text-cyan-200" style={{ fontSize: "clamp(2.5rem, 6vw, 6rem)", lineHeight: 1 }}>
            {state.current_song.song}
          </div>
        )}
      </div>
      <div className="mt-10 max-w-4xl mx-auto space-y-4">
        {top5.map((p, i) => {
          const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `${i+1}.`;
          const styles = i === 0
            ? "border-yellow-300/50 bg-gradient-to-r from-yellow-500/15 to-amber-500/5 shadow-[0_0_60px_-20px_rgba(250,204,21,0.55)]"
            : i === 1 ? "border-zinc-300/40 bg-gradient-to-r from-zinc-300/10 to-zinc-500/5"
            : i === 2 ? "border-orange-400/40 bg-gradient-to-r from-orange-500/10 to-orange-500/5"
            : "border-white/10 bg-white/[0.03]";
          return (
            <div key={i} className={`flex items-center gap-6 rounded-2xl border px-8 py-5 ${styles}`}>
              <div className="font-heading text-5xl w-16">{medal}</div>
              <div className="flex-1 font-heading truncate" style={{ fontSize: "clamp(1.5rem, 3vw, 3rem)" }}>{p.name}</div>
              <div className="font-heading tabular-nums" style={{ fontSize: "clamp(1.5rem, 3vw, 3rem)" }}>{(p.score || 0).toLocaleString("tr-TR")}</div>
            </div>
          );
        })}
        {top5.length === 0 && <div className="text-zinc-500 text-center py-12 text-2xl">Henüz puan yok.</div>}
      </div>

      {!isMini && state.next_song && !state.is_last_song && (
        <div className="mt-10 max-w-3xl mx-auto rounded-2xl border border-violet-400/25 bg-violet-500/5 p-6 text-center">
          <div className="text-violet-300 font-heading tracking-widest text-lg">SIRADAKİ ŞARKI</div>
          <div className="mt-1 text-zinc-400 font-heading tracking-widest">{state.next_song.artist?.toUpperCase()}</div>
          <div className="mt-1 font-literary italic text-4xl text-violet-200">{state.next_song.song}</div>
        </div>
      )}

      <div className="mt-10 flex justify-center">
        <button onClick={onContinue} disabled={busy} data-testid="projection-lb-continue-btn"
                className="rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-black font-heading font-bold px-10 py-4 text-2xl hover:opacity-90 disabled:opacity-40">
          {isMini
            ? (state.current_song?.is_last_idiom_of_song ? "🎵 ŞARKIYI TAMAMLA →" : "SONRAKİ DEYİM →")
            : (state.is_last_song ? "🏁 FİNAL →" : "🎵 SONRAKİ ŞARKIYA GEÇ →")}
        </button>
      </div>
    </div>
  );
}

function ProjectionCountdown({ deadline, onDone }) {
  const [n, setN] = useState(3);
  useEffect(() => {
    const iv = setInterval(() => {
      const s = Math.max(0, Math.ceil((new Date(deadline).getTime() - serverNow()) / 1000));
      setN(s);
      if (s <= 0) { clearInterval(iv); onDone?.(); }
    }, 200);
    return () => clearInterval(iv);
  }, [deadline, onDone]);
  return (
    <div className="mt-20 flex flex-col items-center">
      <div className="font-heading font-bold tabular-nums bg-gradient-to-br from-cyan-300 to-violet-400 bg-clip-text text-transparent leading-none"
           style={{ fontSize: "clamp(10rem, 24vw, 24rem)" }}>
        {n || "!"}
      </div>
      <div className="mt-6 text-zinc-400 font-literary italic text-3xl">Hazır ol…</div>
    </div>
  );
}

